import { getTimeSlotsClass, getSlotList } from "./TarifUtils";
import { useMantineColorScheme } from "@mantine/core";
import * as d3 from "d3";
import { useEffect, useRef, useState, useCallback } from "react";
import './TimeSlotDisplay.css';

// Re-implement the 24h / 15‑minute slot display as an SVG D3 chart
// Features:
// - 24 hours axis from 0..24
// - 96 quarter-hour colored rectangles using same color logic as legacy div grid
// - Adaptive to container width with ResizeObserver fallback
// - Tooltip on hover showing HH:MM and hour index

const TOTAL_MINUTES = 24 * 60; // 1440
const SLOT_MIN = 15; // minutes per slot
const SLOT_COUNT = TOTAL_MINUTES / SLOT_MIN; // 96
const TOP_PADDING = 4;
const CELL_HEIGHT = 36;
const AXIS_HEIGHT = 24; // for hour labels
const MARGIN = { top: TOP_PADDING, right: 10, bottom: AXIS_HEIGHT + 4, left: 12 };

const TimeSlotsDisplay = ({ selectedSeason, activeDemandType }) => {
  const { colorScheme } = useMantineColorScheme();
  const containerRef = useRef(null);
  const svgRef = useRef(null);
  const tooltipRef = useRef(null);
  const tooltipFrameRef = useRef(null);
  const [width, setWidth] = useState(800);

  const activeDemand = selectedSeason?.periods?.[activeDemandType];
  const slotList = activeDemand?.rate; // preserving previous prop usage

  const dark = colorScheme === 'dark';

  // Deep signature (recomputed every render) to reflect nested timeslot changes when parent re-renders
  const slotSignature = (() => {
    if (!Array.isArray(slotList)) return 'none';
    try {
      return slotList.map(item => {
        const parts = [];
        if (Array.isArray(item?.timeSlots)) {
          for (const ts of item.timeSlots) {
            const s = ts?.startTime || {}; const e = ts?.endTime || {};
            parts.push(`${s.hour ?? 'x'}:${s.minute ?? 'x'}-${e.hour ?? 'x'}:${e.minute ?? 'x'}`);
          }
        }
        if (item?.timeSlot) {
          const s = item.timeSlot.startTime || {}; const e = item.timeSlot.endTime || {};
          parts.push(`${s.hour ?? 'x'}:${s.minute ?? 'x'}-${e.hour ?? 'x'}:${e.minute ?? 'x'}`);
        }
        return parts.join('|') + (item.periodColor || '');
      }).join(';');
    } catch { return 'err'; }
  })();

  const buildData = useCallback(() => {
    const data = [];
    const periods = Array.isArray(slotList) ? slotList : [];
    for (let i = 0; i < SLOT_COUNT; i++) {
      const startMin = i * SLOT_MIN; // minutes from 0
      const styleObj = getTimeSlotsClass(startMin, slotList, activeDemand?.periodColor);
      // Find owning period (first match)
      let periodIndex = -1; let periodLabel = ''; let periodColor = styleObj?.backgroundColor || 'transparent';
      for (let p = 0; p < periods.length; p++) {
        const item = periods[p];
        // timeSlots style (array of ranges)
        if (Array.isArray(item?.timeSlots)) {
          const hit = item.timeSlots.some(ts => {
            if (!ts) return false;
            const list = getSlotList(ts);
            return list.includes(startMin);
          });
          if (hit) { periodIndex = p; periodColor = item.periodColor || periodColor; periodLabel = item.periodName || item.name || `Period ${p+1}`; break; }
        }
        // timeSlot single style
        if (item?.timeSlot && periodIndex === -1) {
          const list = getSlotList(item.timeSlot);
            if (list.includes(startMin)) { periodIndex = p; periodColor = item.periodColor || periodColor; periodLabel = item.periodName || item.name || `Period ${p+1}`; break; }
        }
      }
      data.push({
        index: i,
        start: startMin,
        end: startMin + SLOT_MIN,
        color: styleObj?.backgroundColor || 'transparent',
        periodIndex,
        periodLabel,
        periodColor
      });
    }
    return data;
  }, [slotList, activeDemand, slotSignature]);

  // Resize handling
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    function measure() {
      const w = el.getBoundingClientRect().width;
      if (w && Math.abs(w - width) > 2) setWidth(w);
    }
    measure();
    let ro = null;
    if ('ResizeObserver' in window) {
      ro = new ResizeObserver(() => measure());
      ro.observe(el);
    } else {
      window.addEventListener('resize', measure);
    }
    return () => {
      if (ro) ro.disconnect(); else window.removeEventListener('resize', measure);
    };
  }, [width]);

  const draw = useCallback(() => {
    const svgEl = svgRef.current;
    if (!svgEl) return;
    const data = buildData();
    const innerWidth = Math.max(200, width - MARGIN.left - MARGIN.right);
    const height = MARGIN.top + CELL_HEIGHT + MARGIN.bottom;
    const svg = d3.select(svgEl)
      .attr('width', width)
      .attr('height', height)
      .style('background', 'transparent')
      .style('overflow', 'visible');

    svg.selectAll('*').remove();

    const g = svg.append('g').attr('transform', `translate(${MARGIN.left},${MARGIN.top})`);

    const x = d3.scaleLinear().domain([0, TOTAL_MINUTES]).range([0, innerWidth]);

    // Draw quarter-hour rectangles with hover transitions
    g.append('g')
      .attr('class', 'slots')
      .selectAll('rect.slot')
      .data(data)
      .enter()
      .append('rect')
      .attr('class', 'slot')
      .attr('data-period-index', d => d.periodIndex)
      .attr('x', d => x(d.start))
      .attr('y', 0)
      .attr('width', d => Math.max(0, x(d.end) - x(d.start)))
      .attr('height', CELL_HEIGHT)
      .attr('fill', d => d.color)
      .attr('stroke', dark ? '#222' : '#ddd')
      .attr('stroke-width', 0.4)
      .style('cursor', d => (d.periodIndex >= 0 && d.color && d.color !== 'transparent') ? 'pointer' : 'default')
      .style('transition', 'y 140ms ease-out, height 140ms ease-out, stroke-width 120ms ease-out, filter 160ms ease-out')
      .on('mouseenter', (event, d) => {
        if (d.periodIndex < 0 || !d.color || d.color === 'transparent') return;
        const r = d3.select(event.currentTarget);
        r.raise()
          .attr('transform', `translate(0,${-4}) scale(1, ${ (CELL_HEIGHT + 8) / CELL_HEIGHT })`)
          .attr('stroke-width', 1.2)
          .attr('stroke', dark ? '#888' : '#555');
      })
      .on('mousemove', (event, d) => {
        const tooltip = tooltipRef.current; if (!tooltip) return;
        const hour = Math.floor(d.start / 60);
        const minute = d.start % 60;
        const label = `${String(hour).padStart(2,'0')}:${String(minute).padStart(2,'0')}`;
        const isEmpty = d.periodIndex < 0 || !d.periodLabel || !d.color || d.color === 'transparent';
        if (isEmpty) { tooltip.style.display = 'none'; return; }
        // Show first so we can measure size
        tooltip.style.display = 'block';
  const periodInfo = `<div class="tsd-period-info"><span class="tsd-period-swatch" style="background:${d.periodColor}"></span><span class="tsd-period-label">${d.periodLabel}</span></div>`;
  tooltip.innerHTML = `<strong>${label}</strong><br/>${periodInfo}`;
        const container = containerRef.current;
        if (!container) return;
        const containerRect = container.getBoundingClientRect();
        const ttWidth = tooltip.offsetWidth;
        const ttHeight = tooltip.offsetHeight;
        // Cancel any pending animation frame to avoid lagging behind cursor
        if (tooltipFrameRef.current) cancelAnimationFrame(tooltipFrameRef.current);
        tooltipFrameRef.current = requestAnimationFrame(() => {
          // Desired X follows cursor directly
            let xPos = event.clientX - containerRect.left + 12;
            // Clamp horizontally
            const maxX = containerRect.width - ttWidth - 2;
            if (xPos < 0) xPos = 0; else if (xPos > maxX) xPos = maxX;
            // Preferred Y above cursor
            let yPos = event.clientY - containerRect.top - ttHeight - 14;
            if (yPos < 0) yPos = event.clientY - containerRect.top + 16; // place below if not enough room
            tooltip.style.left = `${xPos}px`;
            tooltip.style.top = `${yPos}px`;
        });
      })
      .on('mouseleave', (event, d) => {
        const t = tooltipRef.current; if (t) t.style.display = 'none';
        const r = d3.select(event.currentTarget);
        r.attr('transform', null)
          .attr('stroke-width', 0.4)
          .attr('stroke', dark ? '#222' : '#ddd');
      });

    // Hour grid lines
    const hourGroup = g.append('g').attr('class','hour-lines');
    for (let h = 0; h <= 24; h++) {
      const xPos = x(h * 60);
      hourGroup.append('line')
        .attr('x1', xPos)
        .attr('x2', xPos)
        .attr('y1', 0)
        .attr('y2', CELL_HEIGHT)
        .attr('stroke', dark ? '#444' : '#bbb')
        .attr('stroke-width', h === 0 || h === 24 ? 1 : 0.6)
        .attr('opacity', h === 0 || h === 24 ? 1 : 0.9);
    }

    // Axis at bottom with hour labels
    const xAxis = d3.axisBottom(x)
      .tickValues(d3.range(0, TOTAL_MINUTES + 60, 60))
      .tickFormat(d => d/60);
    // Axis positioned flush with slot bottoms (removed prior 4px gap)
    const axisG = g.append('g')
      .attr('transform', `translate(0,${CELL_HEIGHT})`)
      .call(xAxis);
    axisG.selectAll('path.domain').attr('stroke', dark ? '#555' : '#888');
    axisG.selectAll('line').attr('stroke', dark ? '#333' : '#ccc').attr('y2', 4);
    axisG.selectAll('text')
      .style('fill', dark ? '#eee' : '#222')
      .style('font-size', '11px')
      .style('font-family','Nunito, sans-serif');
    }, [buildData, width, colorScheme, slotSignature]);

  // Ensure only the element directly under the pointer has the hover transform.
  // This avoids sticky transforms when moving the mouse quickly across many rects.
  useEffect(() => {
    const container = containerRef.current;
    const svgEl = svgRef.current;
    if (!container || !svgEl) return;
    function onContainerMove(e) {
      // Clear transforms on all slots
      const rects = svgEl.querySelectorAll('rect.slot');
      rects.forEach(r => {
        r.removeAttribute('transform');
        r.setAttribute('stroke-width', 0.4);
        r.setAttribute('stroke', colorScheme === 'dark' ? '#222' : '#ddd');
      });

      const el = document.elementFromPoint(e.clientX, e.clientY);
      if (!el || !el.classList.contains('slot')) return;
      const periodIndex = Number(el.getAttribute('data-period-index'));
      const fill = el.getAttribute('fill');
      if (Number.isNaN(periodIndex) || periodIndex < 0) return;
      if (!fill || fill === 'transparent') return;
      el.setAttribute('transform', `translate(0,${-4}) scale(1, ${ (CELL_HEIGHT + 8) / CELL_HEIGHT })`);
      el.setAttribute('stroke-width', 1.2);
      el.setAttribute('stroke', colorScheme === 'dark' ? '#888' : '#555');
    }

    function onContainerLeave() {
      const rects = svgEl.querySelectorAll('rect.slot');
      rects.forEach(r => {
        r.removeAttribute('transform');
        r.setAttribute('stroke-width', 0.4);
        r.setAttribute('stroke', colorScheme === 'dark' ? '#222' : '#ddd');
      });
      const t = tooltipRef.current; if (t) t.style.display = 'none';
    }

    container.addEventListener('mousemove', onContainerMove);
    container.addEventListener('mouseleave', onContainerLeave);
    return () => {
      container.removeEventListener('mousemove', onContainerMove);
      container.removeEventListener('mouseleave', onContainerLeave);
    };
  }, [colorScheme, slotSignature]);

  useEffect(() => { draw(); }, [draw]);

  return (
    <div className={`row mt-3 time-slots-root ${dark ? 'dark' : ''}`}>
      <div className="col-sm-12 mb-4">
          <label
            className="me-3 flex-shrink-0 time-slots-label" style={{fontWeight:700}}
            aria-label={`${selectedSeason?.name}: Time Of Use Tariff in 24-Hour Scale`}
          >
            {`${selectedSeason?.name ?? ''}: Time Of Use Tariff in 24-Hour Scale`}
          </label>
          <div ref={containerRef} className="time-slots-container">
            <svg ref={svgRef} role="img" aria-label="24 hour 15 minute slot chart" />
            <div ref={tooltipRef} className="time-slots-tooltip" />
          </div>
        </div>
    </div>
  );
};

export default TimeSlotsDisplay;
